import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { ShieldCheck, Users, Crown, UserMinus, Gift, DollarSign } from "lucide-react";

export function AdminDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null); setErr(false);
    api.get("/admin/stats").then((r) => setData(r.data)).catch(() => setErr(true));
  }, [open]);

  const stats = data ? [
    { key: "total", label: "Total users", value: fmt(data.total_users), icon: Users, color: "#6366F1" },
    { key: "pro", label: "Pro subscribers", value: fmt(data.pro_users), icon: Crown, color: "#F59E0B" },
    { key: "free", label: "Free users", value: fmt(data.free_users), icon: UserMinus, color: "#9CA3AF" },
    { key: "ref", label: "Total referrals", value: fmt(data.total_referrals), icon: Gift, color: "#10B981" },
  ] : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="admin-dialog" className="bg-[#0A0D14] border-white/10 text-[#F9FAFB] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldCheck size={20} className="text-[#10B981]" /> Admin · Overview
          </DialogTitle>
          <DialogDescription className="text-xs text-[#9CA3AF]">
            Read-only snapshot of your audience and revenue.
          </DialogDescription>
        </DialogHeader>

        {err ? (
          <div data-testid="admin-error" className="rounded-xl border border-[#EF4444]/30 bg-[#EF4444]/10 p-4 text-center text-sm text-[#EF4444]">
            Could not load admin stats. Make sure you're signed in as an admin.
          </div>
        ) : !data ? (
          <div data-testid="admin-loading" className="py-10 text-center text-[#6B7280] font-mono-num text-sm">Loading stats…</div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              {stats.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.key} data-testid={`admin-stat-${s.key}`} className="glass rounded-2xl p-4">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">
                      <Icon size={12} style={{ color: s.color }} /> {s.label}
                    </div>
                    <div className="font-mono-num text-2xl font-bold mt-1" style={{ color: s.color }}>{s.value}</div>
                  </div>
                );
              })}
            </div>

            <div data-testid="admin-stat-mrr" className="glass rounded-2xl p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">
                  <DollarSign size={12} className="text-[#10B981]" /> Estimated MRR
                </div>
                <div className="text-[10px] text-[#6B7280] mt-0.5 font-mono-num">{data.pro_users} × ${data.monthly_price}/mo</div>
              </div>
              <div className="font-mono-num text-3xl font-bold text-[#10B981]">${fmt(data.estimated_mrr)}</div>
            </div>

            <p className="text-[10px] text-center text-[#6B7280] font-mono-num">
              Free months granted via referrals: {data.referral_months_earned}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
